import { Button, Modal } from '@ethsign/ui';
import { shortenWalletAddress } from '@ethsign/utils-web';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { CopyText } from '@/components/CopyText';
import { Spin } from '@/components/Loading';
import { useUserInfo } from '@/hooks';
import { getShareId } from '@/services';
import { dateFormat } from '@/utils';

export const ShareContractModal = ({
  open,
  onOpenChange,
  expireDate,
  contractId,
  password,
  type = 'viewer'
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  expireDate?: Date;
  contractId: string;
  password?: string;
  type?: 'viewer' | 'signer';
}) => {
  const { user } = useUserInfo();
  const name = user?.name || user?.ens || shortenWalletAddress(user?.displayAddress || '', 'normal');
  const prefixPath = `${window.location.origin}/`;
  const { data: shareInfo, isLoading } = useQuery({
    queryKey: ['share', contractId],
    queryFn: () => getShareId({ id: contractId }),
    enabled: !!open && !!contractId && type === 'viewer'
  });
  const shareText = `${name} sent you a document to sign. ${
    expireDate ? `Please sign the contract before it expires on ${dateFormat(expireDate, 'YYYY/MM/DD HH:mm:ss')}` : ''
  } Open the link and input the password to sign the contract.
Contract Link: ${prefixPath}contract/${contractId}
${password ? `Contract Password: ${password}` : ''}`;
  const viewText = `${name} sent you a document to view. 
Contract Link: ${prefixPath}share/${shareInfo?.shareId} 
${password ? `Contract Password: ${password}` : ''}`;

  const onShareEmail = () => {
    window.open(
      `mailto:recipient@example.com?subject=${name} sent you a document to ${
        type === 'signer' ? 'sign' : 'view'
      }&body=${encodeURIComponent(type === 'signer' ? shareText : viewText)}`
    );
  };
  return (
    <Modal
      maskClosable={false}
      open={open}
      footer={false}
      className={'md:w-[445px] max-w-max'}
      onOpenChange={onOpenChange}
    >
      <Spin loading={isLoading}>
        <div className="flex items-center flex-col justify-center">
          <div className="text-lg font-medium text-gray-900 mt-[20px] text-center">
            Share Contract {type === 'viewer' ? 'Viewing' : 'Signing'} Link
          </div>
          <div className={'text-sm text-gray-500 text-center mt-2'}>
            Anyone with the link can {type === 'viewer' ? 'view' : 'sign'} the contract.
          </div>
          {type === 'signer' ? (
            <div className="border border-gray-300 rounded-lg mt-5 px-3 py-2 text-gray-900 text-sm">
              {name} sent you a document to sign.{' '}
              {expireDate && (
                <>
                  Please sign the contract before it expires on{' '}
                  <span className={'font-semiBold font-semibold'}>{dateFormat(expireDate, 'YYYY/MM/DD HH:mm:ss')}</span>
                </>
              )}
              <br />
              Open the link and input the password to sign the contract.
              <br />
              <br />
              Contract Link:{' '}
              <a
                href={`${prefixPath}contract/${contractId}`}
                target={'_blank'}
                className={'font-semiBold font-semibold'}
                rel="noreferrer"
              >
                {prefixPath}contract/{contractId}
              </a>{' '}
              <br />
              {password && (
                <>
                  Contract Password: <span className={'font-semiBold font-semibold'}>{password}</span>
                </>
              )}
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg mt-5 px-3 py-2 text-gray-900 text-sm">
              {name} sent you a document to view.
              <br />
              Contract Link:{' '}
              <a
                href={`${prefixPath}share/${shareInfo?.shareId}`}
                target={'_blank'}
                className={'font-semiBold font-semibold'}
                rel="noreferrer"
              >
                {prefixPath}share/{shareInfo?.shareId}
              </a>
              <br />
              {password && (
                <>
                  Contract Password: <span className={'font-semiBold font-semibold'}>{password}</span>
                </>
              )}
            </div>
          )}
          <div className="flex gap-3 mt-5 w-full">
            <Button className={'flex-1 px-0'} variant={'outline'}>
              <CopyText
                className="w-full h-full justify-center"
                text={type === 'signer' ? shareText : viewText}
                iconPosition="left"
                displayText="Copy Message"
              />
            </Button>
            <Button className={'flex-1 px-0'} onClick={onShareEmail}>
              Share with Email
            </Button>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};
